import { useCallback } from 'react';

import { useScrollIntoView } from '@shared/v2/components/InView';

import { useSearchPanelControls } from '@/v2/components/pages/PackageView/contexts/SearchPanelControlsContext';
import { STATUS_MAP } from '@/v2/components/pages/PackageView/hooks/useCtaButton/constants';
import { CtaButtonStatus } from '@/v2/components/pages/PackageView/hooks/useCtaButtonStatus';
import { mainCtaDataLayerEvent } from '@/v2/components/pages/PackageView/utils/dataLayer/dataLayer';
import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import dataLayer from '@/v2/utils/dataLayer';

const useCtaButton = (
  buttonStatus: CtaButtonStatus | null,
  options?: {
    handleReserve: () => void;
    isMobileVariant?: boolean;
  },
) => {
  const { isMobileVariant, handleReserve } = options || {};
  const scrollIntoView = useScrollIntoView();
  const { openCalendar, openMobileSearchPanelModal } = useSearchPanelControls();
  const shouldUseNewEvents = useGA4Events();

  const buttonText = buttonStatus ? STATUS_MAP[buttonStatus].buttonText : null;
  const handleClick = useCallback(() => {
    if (!buttonStatus) {
      return;
    }

    if (shouldUseNewEvents) {
      dataLayer.ctaClickEvent({
        itemText: buttonText || '',
      });
    }

    const { shouldInstantScrollOnMobile, section } =
      STATUS_MAP[buttonStatus] ?? {};

    scrollIntoView(
      section,
      isMobileVariant && shouldInstantScrollOnMobile
        ? undefined
        : { behavior: 'smooth' },
    );

    if (buttonText) {
      mainCtaDataLayerEvent({ action: buttonText });
    }

    if (!isMobileVariant) {
      return;
    }

    switch (buttonStatus) {
      case 'changeDates':
      case 'checkAvailability':
        openMobileSearchPanelModal();
        openCalendar();
        return;
      case 'changeTravellers':
        openMobileSearchPanelModal();
        return;
      case 'reserve':
        handleReserve?.();
        return;
    }
  }, [
    buttonStatus,
    buttonText,
    handleReserve,
    isMobileVariant,
    openCalendar,
    openMobileSearchPanelModal,
    scrollIntoView,
    shouldUseNewEvents,
  ]);

  return {
    buttonText,
    handleClick,
  };
};

export default useCtaButton;
